import {
  ArrowLeftIcon,
  Button,
  CheckIcon,
  cn,
  CopyIcon,
  NotebookIcon,
  Paragraph,
  TextEditor,
  Tooltip,
} from '@quickmd-llc/design-system'
import '@quickmd-llc/design-system/components/text-editor/TextEditor.css'

import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import './styles.scss'
import { useDailyVisit } from 'pages/WaitingRoom/daily/hooks/useDailyVisit'
import { useQueryClient } from '@tanstack/react-query'
import { usePostVisitsVisitIdNotes, useDeleteVisitsVisitIdNotes } from 'api/generated/visit/visit'
import type {
  VisitDoctorApiOutContract,
  VisitMedicalRecordApiOutContract,
} from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'
import {
  getGetVisitsVisitIdV2QueryKey,
  useGetVisitsVisitIdV2,
} from 'api/generated/visit-controller-v2/visit-controller-v2'
import moment from 'moment'
import { htmlToPlainText, removeAllTags, sanitizeNoteHtml } from '../utils'
import { PROVIDER_EVENT_NAMES, tracking } from 'lib/segment/tracking'
import { useSelector } from 'store/hooks/useSelector'
import { getAuthorName } from 'utils/common'

export type VisitNoteViewRef = {
  saveNote: () => Promise<void>
}

/**
 * Reads the authenticated provider's own most-recent visit notes from the V2 visit response.
 * The `PreviousProviderVisitNotes` field is delivered by the backend in QMD-3708; until that
 * change merges and the Orval types regenerate, it isn't on the generated contract yet, so we
 * narrow it at runtime. Once the field exists this keeps working unchanged.
 */
const getPreviousProviderNotes = (visit: VisitDoctorApiOutContract | undefined): VisitMedicalRecordApiOutContract[] => {
  if (visit && 'PreviousProviderVisitNotes' in visit && Array.isArray(visit.PreviousProviderVisitNotes)) {
    return visit.PreviousProviderVisitNotes
  }
  return []
}

export const VisitNoteView = React.forwardRef<VisitNoteViewRef>((_props, ref) => {
  const textEditorRef = useRef(null)
  const { patientWithVisitInfo } = useDailyVisit()
  const { data: visitDetails } = useGetVisitsVisitIdV2(
    patientWithVisitInfo?.VisitId || '',
    { includeMostRecentMatchingVisit: true },
    {
      query: { enabled: false },
    },
  )
  const previousNoteEntry = visitDetails?.PreviousVisit?.Notes?.find((n) => !n.IsAddendum)
  const previousNote = previousNoteEntry?.Message
  const previousDate = previousNoteEntry?.DateTime
  const previousProviderId = visitDetails?.PreviousVisit?.ProviderId
  const currentProviderId = useSelector((state) => state.waitingRoomReducer.providerProfile?.ProviderId)
  const isSameProvider = Boolean(previousProviderId && currentProviderId && previousProviderId === currentProviderId)
  const previousAuthorName = getAuthorName(previousNoteEntry?.Author, false).trim()

  // The authenticated provider's own last note for this patient (same visit type), used by
  // the one-click "Copy Forward" button — distinct from PreviousVisit, which is the most
  // recent visit by any provider.
  const previousProviderNote = getPreviousProviderNotes(visitDetails).find((n) => !n.IsAddendum)?.Message
  const hasPreviousProviderNote = Boolean(previousProviderNote)

  const currentNote = visitDetails?.Notes ? (visitDetails?.Notes.find((n) => !n.IsAddendum)?.Message ?? '') : ''
  const savedNote = useRef<string>(currentNote || '')
  const queryClient = useQueryClient()
  const { mutateAsync: saveNoteMutation } = usePostVisitsVisitIdNotes()
  const { mutateAsync: deleteNoteMutation } = useDeleteVisitsVisitIdNotes()
  const [note, setNote] = useState<string>(currentNote || '')
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const initialNoteRef = useRef<string>(currentNote || '')
  const [isViewingPrevious, setIsViewingPrevious] = useState(false)
  const [hasCopied, setHasCopied] = useState(false)
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null)
  const [hasCopiedForward, setHasCopiedForward] = useState(false)
  const copyForwardTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null)

  useEffect(() => {
    const initial = initialNoteRef.current || ''
    // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
    if (textEditorRef.current && typeof textEditorRef.current.onSetValue === 'function') {
      // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
      textEditorRef.current.onSetValue(initial)
    }
    setNote(initial)
  }, [])

  const onChangeText = (text: string) => {
    setNote(text)
  }

  useEffect(
    () => () => {
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current)
      if (copyForwardTimeoutRef.current) clearTimeout(copyForwardTimeoutRef.current)
    },
    [],
  )

  const handleViewPreviousNotes = useCallback(() => {
    setIsViewingPrevious(true)
    setHasCopied(false)
  }, [])

  const handleBackToCurrentNotes = useCallback(() => {
    setIsViewingPrevious(false)
    setHasCopied(false)
  }, [])

  const handleCopyToClipboard = useCallback(async () => {
    if (!previousNote) return
    try {
      const sanitizedHtml = sanitizeNoteHtml(previousNote)
      // Derive the plain-text payload from the sanitized HTML so legacy
      // plain-text notes keep literal `<`/`>` and rich-text notes lose only
      // their tags (see htmlToPlainText).
      const plainText = htmlToPlainText(sanitizedHtml)

      if (typeof ClipboardItem !== 'undefined' && navigator.clipboard.write) {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([sanitizedHtml], { type: 'text/html' }),
            'text/plain': new Blob([plainText], { type: 'text/plain' }),
          }),
        ])
      } else {
        await navigator.clipboard.writeText(plainText)
      }

      tracking.trackEvent(PROVIDER_EVENT_NAMES.COPY_PREVIOUS_VISIT_NOTE, {
        PatientId: patientWithVisitInfo?.PatientId || '',
        ServiceTypeName: visitDetails?.ServiceTypeName || '',
        VisitId: patientWithVisitInfo?.VisitId || '',
        VisitTypeName: patientWithVisitInfo?.VisitName || '',
      })

      setHasCopied(true)
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current)
      // @ts-expect-error TS2540 -- MIGRATION: not approved, fix when encountered
      copiedTimeoutRef.current = setTimeout(() => setHasCopied(false), 2000)
    } catch {
      // Clipboard write can fail if page is not focused
    }
  }, [
    previousNote,
    patientWithVisitInfo?.PatientId,
    patientWithVisitInfo?.VisitId,
    patientWithVisitInfo?.VisitName,
    visitDetails?.ServiceTypeName,
  ])

  const persistNote = useCallback(
    async (content: string) => {
      if (!patientWithVisitInfo?.VisitId) return
      if (content === savedNote.current) return
      try {
        setIsSaving(true)
        const isEmpty = !content?.trim() || removeAllTags(content).trim().length === 0
        if (isEmpty) {
          setNote('')
          savedNote.current = ''
          await deleteNoteMutation({ visitId: patientWithVisitInfo.VisitId })
          queryClient.setQueryData<VisitDoctorApiOutContract | undefined>(
            getGetVisitsVisitIdV2QueryKey(patientWithVisitInfo.VisitId, { includeMostRecentMatchingVisit: true }),
            (prev) => (prev ? { ...prev, Notes: [] } : prev),
          )
        } else {
          await saveNoteMutation({
            visitId: patientWithVisitInfo.VisitId,
            data: { Message: content },
          })
          savedNote.current = content
          const nowIso = new Date().toISOString()
          queryClient.setQueryData<VisitDoctorApiOutContract | undefined>(
            getGetVisitsVisitIdV2QueryKey(patientWithVisitInfo.VisitId, { includeMostRecentMatchingVisit: true }),
            (prev) => {
              if (!prev) return prev
              const addenda = Array.isArray(prev.Notes) ? prev.Notes.filter((n) => !!n.IsAddendum) : []
              const existingMain = Array.isArray(prev.Notes) ? prev.Notes.find((n) => !n.IsAddendum) : undefined
              const nextMain: VisitMedicalRecordApiOutContract =
                existingMain ?
                  { ...existingMain, Message: content, DateTime: nowIso, IsAddendum: false }
                : { Message: content, DateTime: nowIso, IsAddendum: false }
              return { ...prev, Notes: [...addenda, nextMain] }
            },
          )
        }
      } catch (error) {
        console.error('Failed to save/delete note', error)
      } finally {
        setIsSaving(false)
      }
    },
    [patientWithVisitInfo.VisitId, deleteNoteMutation, queryClient, saveNoteMutation],
  )

  const onSaveNote = useCallback(() => persistNote(note), [persistNote, note])

  const handleCopyForward = useCallback(async () => {
    if (!previousProviderNote) return
    // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
    if (textEditorRef.current && typeof textEditorRef.current.onSetValue === 'function') {
      // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
      textEditorRef.current.onSetValue(previousProviderNote)
    }
    setNote(previousProviderNote)

    setHasCopiedForward(true)
    if (copyForwardTimeoutRef.current) clearTimeout(copyForwardTimeoutRef.current)
    // @ts-expect-error TS2540 -- MIGRATION: not approved, fix when encountered
    copyForwardTimeoutRef.current = setTimeout(() => setHasCopiedForward(false), 2000)

    tracking.trackEvent(PROVIDER_EVENT_NAMES.COPY_FORWARD_VISIT_NOTE, {
      PatientId: patientWithVisitInfo?.PatientId || '',
      ServiceTypeName: visitDetails?.ServiceTypeName || '',
      VisitId: patientWithVisitInfo?.VisitId || '',
      VisitTypeName: patientWithVisitInfo?.VisitName || '',
    })

    await persistNote(previousProviderNote)
  }, [
    previousProviderNote,
    persistNote,
    patientWithVisitInfo?.PatientId,
    patientWithVisitInfo?.VisitId,
    patientWithVisitInfo?.VisitName,
    visitDetails?.ServiceTypeName,
  ])

  useImperativeHandle(ref, () => ({ saveNote: onSaveNote }), [onSaveNote])

  const formattedPreviousDate = previousDate ? moment(previousDate).format('M/D/YYYY') : ''
  const hasPreviousNotes = Boolean(previousDate && previousNote)

  return (
    <div
      className={cn(
        'relative flex h-full min-h-0 w-full flex-col p-sp-2',
        isViewingPrevious ? 'overflow-hidden border border-highlight bg-surface-active' : 'bg-white',
      )}>
      <div className='flex h-sp-4 items-center justify-between gap-sp-1'>
        {isViewingPrevious ?
          <>
            <div className='flex items-center gap-sp-0.5'>
              <NotebookIcon />
              <div className='flex flex-col'>
                <Paragraph className='font-semibold text-text-dark'>
                  {isSameProvider ?
                    `Viewing your ${formattedPreviousDate} notes`
                  : `Viewing notes from ${formattedPreviousDate}`}
                </Paragraph>
                {!isSameProvider && previousAuthorName ?
                  <Paragraph size='xs' className='text-text-light'>
                    By {previousAuthorName}
                  </Paragraph>
                : null}
              </div>
            </div>
            <Button priority='primary' size='s' onClick={handleBackToCurrentNotes}>
              <ArrowLeftIcon />
              Back to current notes
            </Button>
          </>
        : <>
            <div className='flex items-center gap-sp-0.5'>
              <NotebookIcon />
              <Paragraph className='font-semibold text-text-dark'>Notes</Paragraph>
            </div>
            <div className='flex items-center gap-sp-1'>
              {hasPreviousProviderNote ?
                <Tooltip content={hasCopiedForward ? 'Notes copied' : 'Copy my previous notes'}>
                  <Button
                    priority='secondary'
                    size='s'
                    isIcon
                    onClick={handleCopyForward}
                    aria-label='Copy my previous notes'>
                    {hasCopiedForward ?
                      <CheckIcon />
                    : <CopyIcon />}
                  </Button>
                </Tooltip>
              : null}
              {hasPreviousNotes ?
                <Button priority='secondary' size='s' onClick={handleViewPreviousNotes}>
                  <Paragraph className='text-highlight'>
                    {isSameProvider ? 'See your past notes' : 'See past notes'}
                  </Paragraph>
                </Button>
              : null}
            </div>
          </>
        }
      </div>

      <div
        role='presentation'
        className={cn('visit-editor-view-container min-h-0 flex-1 overflow-hidden', isViewingPrevious && 'hidden')}>
        <TextEditor
          enableHTML
          floatingToolbar
          placeHolderText='Start typing...'
          containerAdditionalClassName='visit-view-text-editor flex-1 min-h-0'
          setText={onChangeText}
          ref={textEditorRef}
          saving={isSaving}
          onBlur={onSaveNote}
        />
      </div>

      {isViewingPrevious ?
        <div className='relative flex min-h-0 flex-1 flex-col overflow-hidden'>
          <div className='visit-view-text-editor ds-quill ql-snow flex-1 overflow-y-auto'>
            <div
              className='ql-editor previous-note-readonly'
              dangerouslySetInnerHTML={{ __html: sanitizeNoteHtml(previousNote) }}
            />
          </div>
          {isSameProvider ?
            <div className='absolute bottom-sp-2 left-1/2 z-10 -translate-x-1/2'>
              <Button
                priority='secondary'
                size='s'
                onClick={handleCopyToClipboard}
                className='rounded-full bg-white shadow-md'
                disabled={hasCopied}>
                {hasCopied ?
                  <CheckIcon />
                : <CopyIcon />}
                <Paragraph className={cn('font-semibold', hasCopied ? 'text-text-disabled' : 'text-highlight')}>
                  {hasCopied ? 'Copied!' : 'Copy to clipboard'}
                </Paragraph>
              </Button>
            </div>
          : null}
        </div>
      : null}
    </div>
  )
})

VisitNoteView.displayName = 'VisitNoteView'
